import React, {useEffect, useMemo, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import cn from 'classnames';

import {EEventSource, EEventType} from 'coreClasses/events/IEvents';
import ProductModel from 'coreClasses/product/ProductModel';
import {ERecommendationsTypes, IRecommendationsResponse} from 'coreClasses/recommendations/IRecommendationsMgr';
import {useBannedProducts, useProduct, useProducts} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {IRecommendations} from './IRecommendations';

const Recommendations = ({
	providerId,
	type,
	productId,
	recommendationComponent: RecommendationComponent,
	onRecommendationsProductsLoaded,
	heading,
	...props
}: IRecommendations) => {
	const {ref, inView} = useInView({triggerOnce: true});
	const {managerRepository} = useGeneralProvider();
	const [recommendations, setRecommendations] = useState<IRecommendationsResponse>(null);
	const [isRecommendationsViewed, setIsRecommendationsViewed] = useState(false);
	const recommendationsProvider = managerRepository.getRecommendationsMgr().getProvider(providerId);
	const product = useProduct(productId);
	const fetchedProducts = useProducts(recommendations?.productIds || [], false);
	const availableProducts = useMemo(
		() => fetchedProducts.filter((productModel) => !productModel?.isOffline()) as typeof fetchedProducts,
		[fetchedProducts],
	);
	const {banned, notBanned, bansByProductId} = useBannedProducts(availableProducts);
	const isBanningResolved = availableProducts.length === 0 || banned.length + notBanned.length === availableProducts.length;

	const visibleProducts = useMemo(() => {
		if (!isBanningResolved) {
			return [] as typeof availableProducts;
		}

		return availableProducts.filter((p) => {
			const pid = p?.getProductId();

			return !bansByProductId[pid]?.isBannedA;
		}) as typeof availableProducts;
	}, [availableProducts, bansByProductId, isBanningResolved]);

	const isRecommendationsReady = fetchedProducts?.every((productModel) => productModel?.getProductId());

	useEffect(() => {
		let isMounted = true;

		const getRecommendations = async () => {
			const recommendedProducts = await recommendationsProvider.getRecommendations(type, [product]);

			if (isMounted) {
				setRecommendations(recommendedProducts);
			}

			managerRepository.getEventsMgr().emit(EEventType.RECOMMENDATIONS_LOADED, {
				type: providerId,
			});
		};

		if (productId) {
			product?.isReady() && getRecommendations();
		} else {
			getRecommendations();
		}

		return () => {
			isMounted = false;
		};
	}, [type, productId, product?.isReady()]);

	useEffect(() => {
		if (visibleProducts.length
				&& fetchedProducts.length === recommendations?.productIds?.length
				&& isRecommendationsReady
				&& typeof onRecommendationsProductsLoaded === 'function') {
			onRecommendationsProductsLoaded(visibleProducts, type);
		}
	}, [isRecommendationsReady, visibleProducts.length]);

	useEffect(() => {
		if (inView && !isRecommendationsViewed && recommendations?.productIds?.length) {
			managerRepository.getEventsMgr().emit(EEventType.RECOMMENDATIONS_VIEW, {
				recommenderDetails: recommendations,
			});

			setIsRecommendationsViewed(true);
		}
	}, [inView]);

	const handleRecommendationClick = (product: ProductModel) => {
		managerRepository.getEventsMgr().emit(EEventType.PRODUCT_RECOMMENDATION_CLICK, {
			recommenderDetails: recommendations,
			product,
		});
	};

	return (
		<div className={cn('recommendations-wrapper', {
			'you-may-also-like-wrapper': type === ERecommendationsTypes.YOU_MAY_ALSO_LIKE,
			'style-it-with-wrapper': type === ERecommendationsTypes.COMPLETE_THE_SET,
			'recently-viewed-wrapper': type === ERecommendationsTypes.RECENTLY_VIEWED,
		})} ref={ref}>
			{visibleProducts.length && heading ? heading : <></>}
			<RecommendationComponent
				{...props}
				recommendationType={type}
				maxItems={props.maxItems}
				productId={productId}
				products={visibleProducts}
				onProductClick={handleRecommendationClick}
				source={EEventSource[ERecommendationsTypes[type]]}
			/>
		</div>
	);
};

export default Recommendations;
